<template>
  <div class="editor-backdrop">
    <div class="editor-modal p-3">
      <div class="editor-header">
        <h5><i class="fas fa-paint-brush me-1"></i>編集</h5>
      </div>

      <div class="toolbar">
        <button
          class="btn"
          :class="
            tool === 'brush' ? 'bg-gradient-warning' : 'bg-gradient-light'
          "
          @click="tool = 'brush'"
        >
          <i class="fas fa-paint-brush me-1"></i>
          ブラシ
        </button>

        <button
          class="btn"
          :class="
            tool === 'eraser' ? 'bg-gradient-warning' : 'bg-gradient-light'
          "
          @click="tool = 'eraser'"
        >
          <i class="fas fa-eraser me-1"></i>
          消しゴム
        </button>

        <button class="btn btn-light" @click="clearCanvas">
          <i class="fas fa-trash-alt me-1"></i>
          クリア
        </button>

        <div class="brush-size-control">
          <label class="brush-size-label">
            <i class="fas fa-paint-brush me-1"></i>
            ブラシサイズ
          </label>
          <div class="brush-size-slider">
            <input type="range" min="10" max="100" v-model="brushSize" />
            <span class="brush-size-value"> {{ brushSize }}px </span>
          </div>
        </div>
      </div>

      <div class="image-wrapper">
        <img ref="image" :src="image.url" @load="initializeCanvas" />

        <canvas
          ref="displayCanvas"
          class="editor-canvas"
          @mousedown="startDraw"
          @mousemove="draw"
          @mouseup="stopDraw"
          @mouseleave="stopDraw"
        />
      </div>

      <div
        class="w-100 mt-2 gap-3 d-flex flex-column flex-md-row justify-content-center"
      >
        <div
          class="input-group shadow-lg input-text-wrapper input-wrapper-layout align-items-end"
        >
          <textarea
            v-model="prompt"
            class="form-control border-0 shadow-none"
            placeholder="編集範囲をどのように変更しますか？"
            rows="2"
            @keydown.enter.exact.prevent="generate"
            @keydown.enter.shift.stop
          />
        </div>
        <div class="editor-prompt-actions">
          <button class="btn btn-secondary" @click="$emit('close')">
            <i class="fas fa-times me-1"></i>
            キャンセル
          </button>
          <button class="btn bg-gradient-warning" @click="generate">
            <i class="fas fa-magic me-1"></i>
            生成
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    id: {
      type: Number,
      required: true,
    },
    image: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      tool: 'brush',
      brushSize: 40,
      drawing: false,
      prompt: '',
      displayCtx: null,
      maskCanvas: null,
      maskCtx: null,
      canvasOffsetX: 0,
      canvasOffsetY: 0,
      originalWidth: 0,
      originalHeight: 0,
      scaleX: 1,
      scaleY: 1,
    }
  },

  methods: {
    initializeCanvas() {
      this.$nextTick(() => {
        const img = this.$refs.image
        const canvas = this.$refs.displayCanvas

        if (!img || !canvas) {
          return
        }

        const imgRect = img.getBoundingClientRect()
        const wrapperRect = img.parentElement.getBoundingClientRect()

        const displayWidth = imgRect.width
        const displayHeight = imgRect.height

        // 元画像サイズ
        this.originalWidth = img.naturalWidth
        this.originalHeight = img.naturalHeight

        // 表示Canvas
        canvas.width = displayWidth
        canvas.height = displayHeight
        canvas.style.width = `${displayWidth}px`
        canvas.style.height = `${displayHeight}px`

        const left = imgRect.left - wrapperRect.left
        const top = imgRect.top - wrapperRect.top

        canvas.style.left = `${left}px`
        canvas.style.top = `${top}px`

        this.canvasOffsetX = left
        this.canvasOffsetY = top

        this.displayCtx = canvas.getContext('2d')

        // ★マスクCanvasは元画像サイズ
        if (!this.maskCanvas) {
          this.maskCanvas = document.createElement('canvas')
          this.maskCtx = this.maskCanvas.getContext('2d')
        }

        this.maskCanvas.width = this.originalWidth
        this.maskCanvas.height = this.originalHeight

        this.maskCtx.fillStyle = 'rgba(0,0,0,255)'
        this.maskCtx.fillRect(0, 0, this.originalWidth, this.originalHeight)

        this.scaleX = this.originalWidth / displayWidth

        this.scaleY = this.originalHeight / displayHeight
      })
    },

    getMousePosition(e) {
      const rect = this.$refs.displayCanvas.getBoundingClientRect()

      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }
    },

    startDraw() {
      this.drawing = true
    },

    stopDraw() {
      this.drawing = false
    },

    draw(e) {
      if (!this.drawing) {
        return
      }

      const pos = this.getMousePosition(e)

      if (this.tool === 'brush') {
        this.paint(pos.x, pos.y)
      } else {
        this.erase(pos.x, pos.y)
      }
    },

    paint(x, y) {
      //
      // 表示用
      //
      this.displayCtx.fillStyle = 'rgba(255,98,0,0.25)'

      this.displayCtx.beginPath()
      this.displayCtx.arc(x, y, this.brushSize, 0, Math.PI * 2)
      this.displayCtx.fill()

      //
      // OpenAI用マスク
      //
      const mx = x * this.scaleX
      const my = y * this.scaleY
      const r = this.brushSize * this.scaleX

      this.maskCtx.globalCompositeOperation = 'destination-out'

      this.maskCtx.beginPath()
      this.maskCtx.arc(mx, my, r, 0, Math.PI * 2)
      this.maskCtx.fill()

      this.maskCtx.globalCompositeOperation = 'source-over'
    },

    erase(x, y) {
      this.displayCtx.globalCompositeOperation = 'destination-out'

      this.displayCtx.beginPath()
      this.displayCtx.arc(x, y, this.brushSize, 0, Math.PI * 2)
      this.displayCtx.fill()

      this.displayCtx.globalCompositeOperation = 'source-over'

      const mx = x * this.scaleX
      const my = y * this.scaleY
      const r = this.brushSize * this.scaleX

      this.maskCtx.fillStyle = 'black'
      this.maskCtx.beginPath()
      this.maskCtx.arc(mx, my, r, 0, Math.PI * 2)
      this.maskCtx.fill()
    },

    clearCanvas() {
      this.displayCtx.clearRect(
        0,
        0,
        this.$refs.displayCanvas.width,
        this.$refs.displayCanvas.height,
      )

      this.maskCtx.fillStyle = 'black'

      this.maskCtx.fillRect(0, 0, this.originalWidth, this.originalHeight)
    },

    async generate() {
      // 半透明を完全透明/完全不透明へ変換
      const imageData = this.maskCtx.getImageData(
        0,
        0,
        this.maskCanvas.width,
        this.maskCanvas.height,
      )
      const data = imageData.data

      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3]
        // 少しでも透明なら完全透明
        data[i + 3] = alpha < 255 ? 0 : 255
      }

      this.maskCtx.putImageData(imageData, 0, 0)

      const maskBlob = await new Promise((resolve) => {
        this.maskCanvas.toBlob(resolve, 'image/png')
      })

      this.$emit('generate', {
        id: this.id,
        image: this.image,
        mask: maskBlob,
        prompt: this.prompt,
      })
    },
  },
  mounted() {
    window.addEventListener('resize', this.initializeCanvas)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.initializeCanvas)
  },
}
</script>

<style scoped>
.brush-size-control {
  display: flex;
  flex-direction: column;
  margin-left: 0;
  transform: translateY(-4px);
}

.brush-size-label {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
  line-height: 1;
}

.brush-size-slider {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brush-size-slider input[type='range'] {
  width: 180px;
}

.brush-size-value {
  min-width: 55px;
  font-size: 13px;
  color: #666;
  text-align: left;
}

.editor-prompt-actions {
  display: flex;
  align-items: center;
  gap: 10px; /* ボタン間の余白 */
  flex-shrink: 0;
}

.editor-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.editor-modal {
  width: 95vw;
  height: 95vh;
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-header {
  display: flex;
  justify-content: space-between;
}

.toolbar {
  margin-top: 20px;
  display: flex;
  gap: 14px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.image-wrapper {
  flex: 1;
  position: relative;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;
}

.image-wrapper img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.editor-canvas {
  position: absolute;
  cursor: crosshair;
}

.footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
